import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface SocialPost {
  id: string;
  user_id: string;
  content: string;
  image_url: string | null;
  mission_id: string | null;
  likes_count: number;
  created_at: string;
  profiles: {
    username: string;
    avatar_url: string;
  };
  missions?: {
    title: string;
    points: number;
  };
  liked?: boolean;
}

const mockPosts: SocialPost[] = [
  {
    id: 'post-1',
    user_id: 'user-2',
    content: 'Just completed my daily bike commute! 🚴‍♀️ Feeling great about reducing my carbon footprint.',
    image_url: 'https://images.pexels.com/photos/100582/pexels-photo-100582.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
    mission_id: 'mission-3',
    likes_count: 23,
    created_at: '2024-12-08T08:30:00Z',
    profiles: {
      username: 'GreenGuru',
      avatar_url: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
    },
    missions: {
      title: 'Car-Free Commute',
      points: 100
    },
    liked: false
  },
  {
    id: 'post-2',
    user_id: 'user-3',
    content: 'Made a delicious plant-based Buddha bowl today! 🥗 Who else is trying to eat more sustainably?',
    image_url: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
    mission_id: 'mission-4',
    likes_count: 31,
    created_at: '2024-12-08T12:15:00Z',
    profiles: {
      username: 'EcoFriend',
      avatar_url: 'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
    },
    missions: {
      title: 'Plant-Based Meal',
      points: 60
    },
    liked: true
  }
];

export function useSocialPosts() {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();

  useEffect(() => {
    fetchPosts();
    
    // Set up real-time subscription for new posts from completed missions
    if (isSupabaseConfigured) {
      const subscription = supabase
        .channel('completed_missions')
        .on('postgres_changes', 
          { event: 'INSERT', schema: 'public', table: 'completed_missions' },
          (payload) => {
            console.log('New mission completed:', payload);
            // Refresh posts when a new mission is completed
            fetchPosts();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using mock social posts data');
        setPosts(mockPosts);
        setLoading(false);
        return;
      }

      console.log('Fetching social posts from database...');
      
      // Fetch posts from completed missions (auto-generated posts)
      const { data: completedMissions, error: missionsError } = await supabase
        .from('completed_missions')
        .select(`
          id,
          user_id,
          completed_at,
          points_earned,
          profiles:user_id (username, avatar_url),
          missions:mission_id (title, points, category, icon)
        `)
        .order('completed_at', { ascending: false })
        .limit(20);

      if (missionsError) {
        console.error('Error fetching completed missions:', missionsError);
        setPosts(mockPosts);
        setLoading(false);
        return;
      }

      // Transform completed missions into social posts
      const missionPosts = (completedMissions || []).map(mission => ({
        id: `mission-post-${mission.id}`,
        user_id: mission.user_id,
        content: `Just completed "${mission.missions?.title}"! 🌱 Making a positive impact one mission at a time.`,
        image_url: null,
        mission_id: mission.missions?.title || null,
        likes_count: Math.floor(Math.random() * 20) + 1, // Random likes for demo
        created_at: mission.completed_at,
        profiles: {
          username: mission.profiles?.username || 'EcoWarrior',
          avatar_url: mission.profiles?.avatar_url || 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
        },
        missions: {
          title: mission.missions?.title || 'Eco Mission',
          points: mission.points_earned
        },
        liked: false
      }));

      // Also fetch regular social posts
      const { data: regularPosts, error: postsError } = await supabase
        .from('social_posts')
        .select(`
          *,
          profiles:user_id (username, avatar_url),
          missions:mission_id (title, points)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (postsError) {
        console.error('Error fetching social posts:', postsError);
      }

      // Combine and sort all posts
      const allPosts = [...missionPosts, ...(regularPosts || [])];
      allPosts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      // Check which posts the current user has liked
      if (user && allPosts.length > 0) {
        const postIds = allPosts.map(post => post.id);
        const { data: likesData, error: likesError } = await supabase
          .from('post_likes')
          .select('post_id')
          .eq('user_id', user.id)
          .in('post_id', postIds);

        if (likesError) {
          console.error('Error fetching likes:', likesError);
          setPosts(allPosts);
          setLoading(false);
          return;
        }

        const likedPostIds = new Set(likesData?.map(like => like.post_id) || []);
        
        const postsWithLikes = allPosts.map(post => ({
          ...post,
          liked: likedPostIds.has(post.id)
        }));

        setPosts(postsWithLikes);
      } else {
        setPosts(allPosts);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts(mockPosts);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async (content: string, imageUrl?: string, missionId?: string) => {
    if (!user) return { error: 'User not authenticated' };

    try {
      if (!isSupabaseConfigured) {
        console.log('Mock post creation');
        const newPost: SocialPost = {
          id: `post-${Date.now()}`,
          user_id: user.id,
          content,
          image_url: imageUrl || null,
          mission_id: missionId || null,
          likes_count: 0,
          created_at: new Date().toISOString(),
          profiles: {
            username: profile?.username || 'You',
            avatar_url: profile?.avatar_url || 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
          },
          liked: false
        };
        setPosts(prev => [newPost, ...prev]);
        return { error: null };
      }

      const { error } = await supabase
        .from('social_posts')
        .insert({
          user_id: user.id,
          content,
          image_url: imageUrl,
          mission_id: missionId
        });

      if (error) throw error;

      await fetchPosts();
      return { error: null };
    } catch (error) {
      console.error('Error creating post:', error);
      return { error };
    }
  };

  const toggleLike = async (postId: string) => {
    if (!user) return { error: 'User not authenticated' };

    try {
      if (!isSupabaseConfigured) {
        console.log('Mock like toggle');
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                liked: !post.liked, 
                likes_count: post.liked ? post.likes_count - 1 : post.likes_count + 1 
              }
            : post
        ));
        return { error: null };
      }

      const post = posts.find(p => p.id === postId);
      if (!post) return { error: 'Post not found' };

      if (post.liked) {
        // Unlike
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('user_id', user.id)
          .eq('post_id', postId);

        if (error) throw error;

        // Update likes count
        await supabase
          .from('social_posts')
          .update({ likes_count: Math.max(0, post.likes_count - 1) })
          .eq('id', postId);
      } else {
        // Like
        const { error } = await supabase
          .from('post_likes')
          .insert({
            user_id: user.id,
            post_id: postId
          });

        if (error) throw error;

        // Update likes count
        await supabase
          .from('social_posts')
          .update({ likes_count: post.likes_count + 1 })
          .eq('id', postId);
      }

      await fetchPosts();
      return { error: null };
    } catch (error) {
      console.error('Error toggling like:', error);
      return { error };
    }
  };

  return {
    posts,
    loading,
    createPost,
    toggleLike,
    refetch: fetchPosts
  };
}